/**
 * MatchDeckPicker — headless applier of the deck already chosen on the home page.
 *
 * The deck is picked in the lobby modal and frozen as the seat's pre-entry intent
 * (`startMatch` in main.tsx always writes it), so this seat NEVER asks again: it
 * auto-selects that deck, auto-locks it and renders only a loader. There is
 * deliberately no in-room picker UI — matchmaking cannot start without a chosen
 * deck, so an in-room chooser would be a second place to choose a deck and a
 * screen no ordinary flow ever reaches.
 *
 * When the intent cannot be applied the choice goes BACK to the home page via
 * `onChoiceRequired` rather than opening a local fallback: the home modal stays
 * the single chooser, and the seat can never sit in `choosing_decks` with no way
 * to lock (the server holds the match until both seats lock — server/rooms.ts).
 */
import type { PlayerId } from '@cards/engine';
import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import type { RoomPlayerState, RoomStateMsg, SavedDeckSelectionRef } from '../../server/protocol.js';
import type { DeckApi, DeckLibrary, SavedDeckDto } from './api.js';
import { createBrowserDeckApi } from './http.js';
import { isAbortError } from './messages.js';
import type { DeckReturnContext, DeckReturnStorage } from './returnContext.js';
import { DelayedLoader } from '../ui/kit/index.js';
import { navigateTo } from '../client/navigation.js';

export interface MatchDeckPickerProps {
  room: RoomStateMsg;
  viewerId: PlayerId;
  /** Kept for the match-entry contract in main.tsx; this seat never navigates to the editor. */
  returnContext?: DeckReturnContext;
  onSelect: (deck: SavedDeckSelectionRef) => void;
  onLock: () => void;
  api?: DeckApi;
  storage?: DeckReturnStorage;
  navigate?: (path: string) => void;
  /** Explicit choice made by the player before opening the game socket. */
  preparedSelection?: SavedDeckSelectionRef;
  /** Clears the crash-safe pre-entry intent only after authoritative lock. */
  onPreparedApplied?: () => void;
  /**
   * The intent could not be applied, so the choice returns to the home page.
   * Implementations clear the stale intent and navigate away from the room.
   */
  onChoiceRequired?: () => void;
  error?: string;
  /** Monotonic authoritative rejection identity; reason text alone is not unique. */
  rejectionClientSeq?: number;
  /** Authoritative actor-only ACK carried by the existing events envelope. */
  acknowledgedClientSeq?: number;
}

interface PendingSelection {
  readonly deck: SavedDeckSelectionRef;
  readonly prepared: boolean;
  readonly errorAtClick?: string;
  readonly rejectionClientSeqAtClick?: number;
  readonly acknowledgedClientSeqAtClick?: number;
}

function seat(room: RoomStateMsg, playerId: PlayerId): RoomPlayerState | undefined {
  return room.players.find((player) => player.playerId === playerId);
}

function defaultNavigate(path: string): void {
  navigateTo(path);
}

const browserApi = createBrowserDeckApi();

export function MatchDeckPicker({
  room,
  viewerId,
  onSelect,
  onLock,
  api = browserApi,
  navigate = defaultNavigate,
  preparedSelection,
  onPreparedApplied,
  onChoiceRequired,
  error,
  rejectionClientSeq,
  acknowledgedClientSeq,
}: MatchDeckPickerProps): ReactElement {
  const [library, setLibrary] = useState<DeckLibrary>();
  const [pending, setPending] = useState<PendingSelection>();
  const [confirmedDeckId, setConfirmedDeckId] = useState<string>();
  const [preparedConfirmed, setPreparedConfirmed] = useState(false);
  const libraryControllerRef = useRef<AbortController | undefined>(undefined);
  const preparedAttemptRef = useRef<string | undefined>(undefined);
  const preparedLockSentRef = useRef(false);
  const preparedAppliedRef = useRef(false);
  const choiceRequiredRef = useRef(false);
  const viewer = seat(room, viewerId);
  const opponent = room.players.find((player) => player.playerId !== viewerId);
  const waitingForOpponent = room.phase === 'waiting_for_players' || opponent === undefined;
  const canSelect = room.phase === 'choosing_decks'
    && viewer?.locked === false
    && !waitingForOpponent;
  const canLock = canSelect && viewer?.selected === true && pending === undefined;

  // `requireChoice` must stay referentially stable: it is a dependency of
  // `loadLibrary`, and re-creating it on every room update would refetch the
  // whole library each time a seat flag changes. So the guard reads live values
  // through a ref instead of closing over them.
  const liveRef = useRef({ locked: false, selected: false, navigate, onChoiceRequired });
  liveRef.current = {
    locked: viewer?.locked === true,
    selected: viewer?.selected === true,
    navigate,
    onChoiceRequired,
  };

  // Sending the player home is terminal for this mount: it must win exactly once,
  // and never after the seat already committed a deck to the server.
  const requireChoice = useCallback((): void => {
    const live = liveRef.current;
    if (choiceRequiredRef.current || live.locked || live.selected) return;
    choiceRequiredRef.current = true;
    if (live.onChoiceRequired !== undefined) live.onChoiceRequired();
    else live.navigate('/');
  }, []);

  const loadLibrary = useCallback((): void => {
    libraryControllerRef.current?.abort();
    const controller = new AbortController();
    libraryControllerRef.current = controller;
    void api.list(false, controller.signal).then((nextLibrary) => {
      if (controller.signal.aborted) return;
      setLibrary(nextLibrary);
    }, (reason: unknown) => {
      if (controller.signal.aborted) return;
      // Without the library the intent cannot be resolved at all, so the choice
      // belongs back on the home page instead of a local retry screen.
      if (!isAbortError(reason)) requireChoice();
    });
  }, [api, requireChoice]);

  useEffect(() => {
    loadLibrary();
    return () => {
      libraryControllerRef.current?.abort();
      libraryControllerRef.current = undefined;
    };
  }, [loadLibrary]);

  useEffect(() => {
    if (pending === undefined) return;
    if (acknowledgedClientSeq !== undefined
      && acknowledgedClientSeq !== pending.acknowledgedClientSeqAtClick) {
      setConfirmedDeckId(pending.deck.deckId);
      setPreparedConfirmed(pending.prepared);
      setPending(undefined);
      return;
    }
    if (rejectionClientSeq !== undefined
      && rejectionClientSeq !== pending.rejectionClientSeqAtClick) {
      setPreparedConfirmed(false);
      setPending(undefined);
      requireChoice();
      return;
    }
    if (error !== undefined && error !== pending.errorAtClick) {
      setPreparedConfirmed(false);
      setPending(undefined);
      requireChoice();
      return;
    }
  }, [acknowledgedClientSeq, error, pending, rejectionClientSeq, requireChoice]);

  const select = useCallback((deck: SavedDeckDto, prepared = false): void => {
    if (!canSelect || pending !== undefined || !deck.validation.ready || deck.archivedAt !== null) return;
    const ref = { deckId: deck.id, revision: deck.revision };
    setPreparedConfirmed(false);
    setPending({
      deck: ref,
      prepared,
      ...(error === undefined ? {} : { errorAtClick: error }),
      ...(rejectionClientSeq === undefined ? {} : { rejectionClientSeqAtClick: rejectionClientSeq }),
      ...(acknowledgedClientSeq === undefined
        ? {}
        : { acknowledgedClientSeqAtClick: acknowledgedClientSeq }),
    });
    onSelect(ref);
  }, [acknowledgedClientSeq, canSelect, error, onSelect, pending, rejectionClientSeq]);

  // No pre-entry intent means this seat was reached without the home page — the
  // only place a deck is chosen. Bounce immediately; nothing here can recover it.
  useEffect(() => {
    if (preparedSelection === undefined) requireChoice();
  }, [preparedSelection, requireChoice]);

  useEffect(() => {
    if (preparedSelection === undefined || library === undefined || !canSelect
      || viewer?.selected === true || pending !== undefined) return;
    const key = `${preparedSelection.deckId}:${preparedSelection.revision}`;
    if (preparedAttemptRef.current === key) return;
    preparedAttemptRef.current = key;
    const usable = (deck: SavedDeckDto): boolean =>
      deck.archivedAt === null && deck.validation.ready;
    // The exact revision first; failing that, the SAME deck at its current
    // revision. Editing the chosen deck after picking it must not turn into a
    // second question — it is still the deck the player chose on the home page.
    const preparedDeck = library.decks.find((deck) =>
      deck.id === preparedSelection.deckId
      && deck.revision === preparedSelection.revision
      && usable(deck))
      ?? library.decks.find((deck) => deck.id === preparedSelection.deckId && usable(deck));
    if (preparedDeck === undefined) {
      requireChoice();
      return;
    }
    select(preparedDeck, true);
  }, [canSelect, library, pending, preparedSelection, requireChoice, select, viewer?.selected]);

  useEffect(() => {
    if (preparedSelection === undefined
      || !preparedConfirmed
      || confirmedDeckId !== preparedSelection.deckId
      || !canLock
      || preparedLockSentRef.current) return;
    preparedLockSentRef.current = true;
    onLock();
  }, [canLock, confirmedDeckId, onLock, preparedConfirmed, preparedSelection]);

  useEffect(() => {
    if (preparedSelection === undefined || viewer?.locked !== true
      || preparedAppliedRef.current) return;
    preparedAppliedRef.current = true;
    onPreparedApplied?.();
  }, [onPreparedApplied, preparedSelection, viewer?.locked]);

  // One continuous loading state from lobby click to first board frame: the same
  // loader the challenge flow already shows for its server-owned decks (App.tsx).
  return <DelayedLoader testId="match-start-loader" />;
}
